import React, { useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL, pathTherapistHome } from "../../config/constants";
import avatarMap from "../../assets/avatars";
import AppSidebar from "../../components/AppSidebar";
import AddPatientModal from "../../components/addPatientModal";
import {
  Layout, Main, ContentWrapper, PageTitle,
  Card, UserHeader, UserAvatar, UserId,
  FieldGrid, FieldItem, FieldLabel, FieldValue,
  ActionBar, EditButton, DeleteButton,
} from "./styles";

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-GB");
};

const PatientProfile = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const token = localStorage.getItem("token");

  const [patient, setPatient] = useState(location.state?.patient || null);
  const [loading, setLoading] = useState(!location.state?.patient);
  const [deleting, setDeleting] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const loadPatient = () => {
    fetch(`${API_URL}/patients/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => { if (!data.error) setPatient(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPatient(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!window.confirm(t("profile.deleteConfirm"))) return;
    setDeleting(true);
    try {
      await fetch(`${API_URL}/patients/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      history.push(pathTherapistHome);
    } catch {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <AppSidebar patient={null} activeNav="profile" />
        <Main><p>{t("common.loading")}</p></Main>
      </Layout>
    );
  }

  if (!patient) {
    return (
      <Layout>
        <AppSidebar patient={null} activeNav="profile" />
        <Main><p>{t("profile.notFound")}</p></Main>
      </Layout>
    );
  }

  return (
    <Layout>
      <AppSidebar patient={patient} activeNav="profile" />
      <Main>
        <ContentWrapper>
        <PageTitle>{t("profile.title")}</PageTitle>
        <Card>
          <UserHeader>
            <UserAvatar
              src={avatarMap[patient.avatar_url] || Object.values(avatarMap)[0]}
              alt="avatar"
            />
            <UserId>
              {t("profile.userId")}: <span>{patient.patient_code}</span>
            </UserId>
          </UserHeader>

          <FieldGrid>
            <FieldItem>
              <FieldLabel>{t("addPatient.sex")}</FieldLabel>
              <FieldValue>{patient.sex || "—"}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.treatmentStartDate")}</FieldLabel>
              <FieldValue>{fmtDate(patient.treatment_start_date)}</FieldValue>
            </FieldItem>

            <FieldItem>
              <FieldLabel>{t("addPatient.dateOfBirth")}</FieldLabel>
              <FieldValue>{fmtDate(patient.date_of_birth)}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.ageAtStart")}</FieldLabel>
              <FieldValue>{patient.age_at_start_corrected || "—"}</FieldValue>
            </FieldItem>

            <FieldItem>
              <FieldLabel>{t("addPatient.isPremature")}</FieldLabel>
              <FieldValue>{patient.is_premature ? t("profile.yes") : t("profile.no")}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.assignedTherapist")}</FieldLabel>
              <FieldValue>{patient.assigned_therapist_name || "—"}</FieldValue>
            </FieldItem>

            {patient.is_premature && (
              <FieldItem>
                <FieldLabel>{t("addPatient.expectedDelivery")}</FieldLabel>
                <FieldValue>{fmtDate(patient.expected_delivery_date)}</FieldValue>
              </FieldItem>
            )}
            <FieldItem>
              <FieldLabel>{t("addPatient.pathology")}</FieldLabel>
              <FieldValue>{patient.pathology || "—"}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.secondaryDiagnosis")}</FieldLabel>
              <FieldValue>{patient.secondary_diagnosis || "—"}</FieldValue>
            </FieldItem>

            <FieldItem>
              <FieldLabel>{t("addPatient.cohort")}</FieldLabel>
              <FieldValue>{patient.cohort ?? "—"}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.notes")}</FieldLabel>
              <FieldValue>{patient.notes_observations || "—"}</FieldValue>
            </FieldItem>
          </FieldGrid>

          <ActionBar>
            <EditButton type="button" onClick={() => setShowEdit(true)}>{t("profile.editPatient")}</EditButton>
            <DeleteButton type="button" onClick={handleDelete} disabled={deleting}>
              {t("profile.deletePatient")}
            </DeleteButton>
          </ActionBar>
        </Card>
        </ContentWrapper>
      </Main>
      {showEdit && (
        <AddPatientModal
          patient={patient}
          onClose={() => setShowEdit(false)}
          onUpdated={() => { setShowEdit(false); setLoading(true); loadPatient(); }}
        />
      )}
    </Layout>
  );
};

export default PatientProfile;
